import React from "react";
import { Alert, Col, Row, Table } from "react-bootstrap";
import { BaseModel } from "sjs-base-model";
import FormatUtility from "utilities/FormatUtility";
import IconNameEnum from "../../../constants/IconNameEnum";
import BaseFile from "../base-file/BaseFile";
import BaseButton from "../base-form/BaseButton";
import BaseCheckbox from "../base-form/BaseCheckbox";
import BaseIcon from "../base-form/BaseIcon";
import BasePagination from "./BasePagination";
import DateFormatEnum from "constants/DateFormatEnum";

interface IBaseTable<R extends BaseModel> {
    getRow(el: R, i: number): JSX.Element[];
    getFooterActions?(selectedItems: number[]): JSX.Element;
    headers: string[];
    headersOrderable?: { name: keyof R; orderable: boolean }[];
    tableColumnsHidden?: number[];
    pageSize?: number;
    items: R[];
    pagination: boolean;
    esporta?: boolean;
    esportaName?: string;
    classNameTh?: string;
    classNameTr?: string;
    classNameTd?: string;
    classNameTf?: string;
    hideTotal?: boolean;
    small?: boolean;
    fullLastColumn?: boolean;
    getExcel?: (progressHandler: (progress: number) => void) => Promise<Blob>;
    defaultOrdered?: { colName: keyof R; direction: undefined | "A" | "D" };
    footer?: string[];
    activeIndex?: number;
    handleClick?: (item?: R) => void;
    className?: string;
    active?: boolean;
    showElementi?: boolean;
    esportaIcon?: IconNameEnum;
    borderTabel?: boolean;
    cellActiveIndex?: number;
    striped?: boolean;
    orderIconColor?: "white" | "default";
    handleTableSort?: (name?: keyof R, direction?: undefined | "A" | "D") => void;
    shadow?: boolean;
    activeRows?: number[];
}

interface ISelectableItems<R extends BaseModel> {
    itemsSelection?: boolean;
    showItemSelection?(el: R): boolean;
    modelId: keyof R;
}

interface IProps<R extends BaseModel> extends IBaseTable<R>, ISelectableItems<R> {}
interface IState<R> {
    firstElem: number;
    lastElem: number;
    selectedItems: number[];
    selectAll: boolean;
    headersOrdered: { colName: keyof R; direction: undefined | "A" | "D" }[];
}

export default class BaseTable<R extends BaseModel> extends React.Component<IProps<R>, IState<R>> {
    constructor(props: IProps<R>) {
        super(props);
        this.state = {
            firstElem: 0,
            lastElem: 0,
            selectedItems: [] as number[],
            selectAll: false,
            headersOrdered: props.defaultOrdered
                ? [props.defaultOrdered]
                : ([] as { colName: keyof R; direction: undefined | "A" | "D" }[]),
        } as IState<R>;
    }

    private hasUnmounted = false;
    private baseFileRef: React.RefObject<BaseFile> = React.createRef<BaseFile>();

    public componentWillUnmount() {
        this.hasUnmounted = true;
    }

    private paginationUpdate = (firstElem: number, lastElem: number) => {
        if (this.hasUnmounted === false) {
            this.setState({
                firstElem: firstElem,
                lastElem: lastElem,
            });
        }
    };

    private getRisultatiLabel = () => {
        const { items, hideTotal } = this.props;
        if (hideTotal) {
            return <></>;
        }
        let lblRisultati = <span>{"Nessun risultato trovato"}</span>;
        if (items.length === 1) {
            lblRisultati = (
                <>
                    <span>{"La ricerca ha prodotto"}</span>
                    <span className={"font-weight-bold"}>{` ${items.length} `}</span>
                    <span>{"risultato"}</span>
                </>
            );
        } else if (items.length > 1) {
            lblRisultati = (
                <>
                    <span>{"La ricerca ha prodotto"}</span>
                    <span className={"font-weight-bold"}>{` ${items.length} `}</span>
                    <span>{"risultati"}</span>
                </>
            );
        }
        return lblRisultati;
    };

    private selectItem = (id: number) => {
        let { selectedItems } = { ...this.state };
        let index = selectedItems.indexOf(id);
        if (index > -1) {
            selectedItems.splice(index, 1);
        } else {
            selectedItems = [...selectedItems, id];
        }
        if (this.hasUnmounted === false) {
            this.setState({ selectedItems: [...selectedItems], selectAll: false });
        }
    };

    private selectAll = () => {
        const { firstElem, lastElem, selectAll } = this.state;

        if (selectAll) {
            if (this.hasUnmounted === false) {
                this.setState({ selectedItems: [] as number[], selectAll: false });
            }
        } else {
            let selected = this.props.items
                .slice(firstElem, lastElem)
                .filter((el) => this.props.showItemSelection === undefined || this.props.showItemSelection(el))
                .map((el) => {
                    const id = el[this.props.modelId];
                    if (typeof id === "number") {
                        return id;
                    } else {
                        return 0;
                    }
                });
            if (this.hasUnmounted === false) {
                this.setState({ selectedItems: selected, selectAll: true });
            }
        }
    };

    private downloadExcel = () => {
        if (this.props.getExcel) {
            const filename = this.props.esportaName || "Elenco risultati ricerca del {1}" || "Report {1}";
            this.baseFileRef.current?.downloadFile(
                this.props.getExcel,
                filename.replace(
                    "{1}",
                    FormatUtility.dateInGivenFormat(new Date(), DateFormatEnum.ISO_DATE_TIME_SECONDS)
                ) + ".xlsx"
            );
        }
    };

    private setOrderByColumn = (colName: keyof R) => {
        let headersOrdered = [...this.state.headersOrdered];
        const { handleTableSort } = this.props;
        // sorting direction
        let direction: undefined | "A" | "D" = "D";
        const header = headersOrdered.find((x) => x.colName === colName);
        if (header && header.direction === "A") {
            direction = "D";
        }
        if (header && header.direction === "D") {
            direction = "A";
        }
        // removes ALL existing
        headersOrdered = [] as { colName: keyof R; direction: undefined | "A" | "D" }[];
        if (direction) {
            // add sorting
            headersOrdered.push({ direction, colName });
        }
        if (this.hasUnmounted === false) {
            this.setState({
                headersOrdered,
            });
        }
        handleTableSort && handleTableSort(colName, direction);
    };

    private getItems = (items: R[], firstElem: number, lastElem: number) => {
        const { headersOrdered } = this.state;
        // sort items
        // headersOrdered.forEach((order) => {
        //     const { colName, direction } = order;
        //     if (direction) {
        //         items = FormatUtility.getSortedArray(items, colName, direction);
        //     }
        // });
        // paginate items
        items = items.slice(firstElem, lastElem);
        return items;
    };

    private getOrderIcon = (direction: undefined | "A" | "D") => {
        switch (direction) {
            case "A":
                return IconNameEnum.ColonnaOrdineAsc;
            case "D":
                return IconNameEnum.ColonnaOrdineDesc;
            default:
                return IconNameEnum.ColonnaOrdineDefault;
        }
    };

    private doNothing = () => {};

    private handleItemCLick = (item?: R) => {
        const { handleClick } = this.props;
        handleClick && handleClick(item);
    };

    public render() {
        const {
            getRow,
            getFooterActions,
            headers,
            classNameTh,
            classNameTr,
            classNameTd,
            items,
            pagination,
            esporta,
            hideTotal,
            small,
            fullLastColumn,
            headersOrderable,
            tableColumnsHidden,
            footer,
            classNameTf,
            activeIndex,
            className,
            active,
            pageSize,
            showElementi,
            borderTabel,
            cellActiveIndex,
            striped,
            orderIconColor,
            shadow,
            activeRows,
        } = this.props;
        const { itemsSelection, modelId, showItemSelection, esportaIcon } = this.props;
        const { firstElem, lastElem, selectAll, selectedItems, headersOrdered } = this.state;

        return (
            <>
                {hideTotal !== true || esporta === true ? (
                    <Row className="mb-2">
                        <Col className="align-self-center">
                            {items.length === 0 ? (
                                <Alert variant="warning" className="ccq-shadow">
                                    {"Nessun risultato trovato"}
                                </Alert>
                            ) : (
                                this.getRisultatiLabel()
                            )}
                        </Col>
                        {esporta && items.length ? (
                            <Col xs={3}>
                                <BaseButton
                                    type={"button"}
                                    color={"light"}
                                    label={""}
                                    icon={esportaIcon}
                                    onClickHandler={this.downloadExcel}
                                    circled={true}
                                    hoverTitle={"Esporta risultati"}
                                    className="float-right"
                                />
                                <BaseFile ref={this.baseFileRef} />
                            </Col>
                        ) : null}
                    </Row>
                ) : null}
                {items.length ? (
                    <>
                        <div
                            className={["rounded mb-3", borderTabel ? "border" : "", shadow ? "ccq-shadow" : ""].join(
                                ""
                            )}
                        >
                            <Table
                                responsive
                                bordered
                                striped={striped}
                                hover
                                size="sm"
                                className={[className, "text-center mb-0", small ? "small" : ""].join(" ")}
                            >
                                <thead className="bg-ccq-blue text-white">
                                    <tr className="nowrap">
                                        {itemsSelection && (
                                            <th className={[classNameTh, "p-2 align-middle"].join(" ")} scope="col">
                                                <BaseCheckbox
                                                    controlId={"baseTable.selectAll"}
                                                    id={"selectAll"}
                                                    name={"all"}
                                                    hoverTitle={"Seleziona tutto"}
                                                    value={selectAll}
                                                    required={false}
                                                    isInvalid={false}
                                                    onChangeHandler={this.selectAll}
                                                    className={
                                                        showItemSelection === undefined ||
                                                        items.some((item) => showItemSelection(item))
                                                            ? ""
                                                            : "cursor-disabled"
                                                    }
                                                    disabled={
                                                        showItemSelection === undefined ||
                                                        items.some((item) => showItemSelection(item))
                                                            ? false
                                                            : true
                                                    }
                                                />
                                            </th>
                                        )}
                                        {headers.map((header: string, elIndex: number) => {
                                            if (
                                                tableColumnsHidden !== undefined &&
                                                tableColumnsHidden.includes(elIndex) === true
                                            ) {
                                                return <React.Fragment key={elIndex}></React.Fragment>;
                                            }
                                            const orderable =
                                                headersOrderable !== undefined &&
                                                elIndex in headersOrderable &&
                                                headersOrderable[elIndex].orderable === true;
                                            const colName =
                                                headersOrderable !== undefined && elIndex in headersOrderable
                                                    ? headersOrderable[elIndex].name
                                                    : undefined;
                                            const ordered =
                                                headersOrdered.find((x) => x.colName === colName)?.direction ??
                                                undefined;
                                            return (
                                                <th
                                                    key={elIndex}
                                                    className={[
                                                        classNameTh,
                                                        orderable ? "cursor-pointer" : "",
                                                        "p-2 align-middle",
                                                    ].join(" ")}
                                                    scope="col"
                                                    onClick={
                                                        orderable && colName
                                                            ? () => this.setOrderByColumn(colName)
                                                            : undefined
                                                    }
                                                >
                                                    {header}
                                                    {orderable ? (
                                                        <BaseIcon
                                                            className="ml-1 align-center icon-font-size "
                                                            icon={this.getOrderIcon(ordered)}
                                                            color={orderIconColor ? orderIconColor : "white"}
                                                        />
                                                    ) : null}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.getItems(items, firstElem, lastElem).map((row: R, rowIndex: number) => (
                                        <tr
                                            key={rowIndex}
                                            className={[
                                                classNameTr,
                                                activeRows?.includes(rowIndex) || active ? "" : "bg-ocq-grey",
                                            ].join("")}
                                        >
                                            {itemsSelection && (
                                                <td className={[classNameTd, "align-middle"].join(" ")}>
                                                    <BaseCheckbox
                                                        controlId={"baseTable.selectElement" + rowIndex}
                                                        id={"selectElement" + rowIndex}
                                                        name={"selected" + rowIndex}
                                                        hoverTitle={"Seleziona"}
                                                        value={selectedItems.indexOf(row[modelId.toString()]) > -1}
                                                        required={false}
                                                        isInvalid={false}
                                                        onChangeHandler={() => this.selectItem(row[modelId.toString()])}
                                                        className={
                                                            showItemSelection === undefined || showItemSelection(row)
                                                                ? ""
                                                                : "cursor-disabled"
                                                        }
                                                        disabled={
                                                            showItemSelection === undefined || showItemSelection(row)
                                                                ? false
                                                                : true
                                                        }
                                                    />
                                                </td>
                                            )}
                                            {getRow(row, rowIndex)
                                                .filter(
                                                    (cell, cellIndex) =>
                                                        tableColumnsHidden === undefined ||
                                                        tableColumnsHidden.includes(cellIndex) === false
                                                )
                                                .map((cell, cellIndex, cells) => (
                                                    <td
                                                        key={cellIndex}
                                                        className={[
                                                            classNameTd,
                                                            "align-middle",
                                                            cellIndex === cellActiveIndex &&
                                                            (activeRows?.includes(rowIndex) || active)
                                                                ? "cursor-pointer text-ocq-blue font-weight-bold"
                                                                : "",
                                                            fullLastColumn && cells.length === cellIndex + 1
                                                                ? "nowrap"
                                                                : "",
                                                        ].join(" ")}
                                                        onClick={
                                                            cellIndex === cellActiveIndex &&
                                                            (activeIndex === rowIndex || active)
                                                                ? () => this.handleItemCLick(row)
                                                                : this.doNothing
                                                        }
                                                    >
                                                        {cell}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className={classNameTf}>
                                    <tr className="nowrap">
                                        {footer?.map((header: string, elIndex: number) => {
                                            if (
                                                tableColumnsHidden !== undefined &&
                                                tableColumnsHidden.includes(elIndex) === true
                                            ) {
                                                return <React.Fragment key={elIndex}></React.Fragment>;
                                            }
                                            return (
                                                <td
                                                    key={elIndex}
                                                    className={[classNameTd, "cursor-disabled"].join(" ")}
                                                    scope="col"
                                                >
                                                    {footer}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                        <Row>
                            <Col>{getFooterActions && getFooterActions(selectedItems)}</Col>
                            <Col hidden={pagination === false}>
                                <BasePagination
                                    visible={pagination}
                                    total={items.length}
                                    paginationUpdate={this.paginationUpdate}
                                    pageSize={pageSize}
                                    showElementi={showElementi}
                                />
                            </Col>
                        </Row>
                    </>
                ) : null}
            </>
        );
    }
}
